import { InfoIcon } from '@assets/icons';
import {
  Button,
  HStack,
  IconButton,
  Stack,
  Tooltip,
  Textarea,
  Text,
  Spinner
} from '@chakra-ui/react';
import {
  generatePersonalPrompt,
  generateTenantPrompt
} from '@services/query-services/lambda.query-service';
import { useUserStore } from '@store/app.store';
import { useBreakpoints } from '@util/hooks';
import { useState } from 'react';
import { Controller } from 'react-hook-form';

/**
 * @param {Object} props
 * @param {import('react-hook-form').UseForm<OnboardingFormValues>} props.form
 */
const PromptsStep = ({ form }) => {
  const user = useUserStore((state) => state.user);
  const tenant = useUserStore((state) => state.tenant);

  const [isWritingPersonalPrompt, setIsWritingPersonalPrompt] = useState(false);
  const [isWritingTenantPrompt, setIsWritingTenantPrompt] = useState(false);
  const { isMobile } = useBreakpoints();

  const writePersonalPromptWithAI = async () => {
    setIsWritingPersonalPrompt(true);
    try {
      const currentContext = form.getValues('context');
      const prompt = await generatePersonalPrompt({
        person: user,
        tenant,
        userDetails: {
          jobTitle: user.title,
          department: user.departments.length ? user.departments[0] : undefined
        },
        skipPatch: true,
        currentContext
      });
      form.setValue('context', prompt);
    } catch (e) {
      console.log('error generating personal prompt', e);
    }

    setIsWritingPersonalPrompt(false);
  };

  const writeTenantPromptWithAI = async () => {
    setIsWritingTenantPrompt(true);
    try {
      const currentContext = form.getValues('tenant_context');
      const prompt = await generateTenantPrompt({
        person: user,
        tenant,
        currentContext
      });
      form.setValue('tenant_context', prompt);
    } catch (e) {
      console.log('error generating tenant prompt', e);
    }

    setIsWritingTenantPrompt(false);
  };

  return (
    <Stack
      gap={16}
      py={8}
      w='full'
      overflowY={!isMobile && 'scroll'}
    >
      <PromptStack
        form={form}
        name='context'
        label='YOUR PERSONAL PROMPT'
        description='Accentuate your experience by telling FORA what matters most to you.'
        tooltip='This will be used to influence tone, style, and content density in your summaries.'
        placeholder='I prefer summaries that are concise and to the point, with a focus on action items and key takeaways.'
        onAiClick={writePersonalPromptWithAI}
        isLoading={isWritingPersonalPrompt}
      />
      {user.type === 'Admin' && (
        <PromptStack
          form={form}
          name='tenant_context'
          label='YOUR ORGANIZATION'
          description='Help FORA understand what is important to know about your organization.'
          tooltip='FORA will use this information to contextually improve summaries and insights.'
          placeholder={`Our company, ${
            tenant?.name || 'Acme Corp'
          }, operates in the technology sector, specializing in software development and IT services. We value innovation, collaboration, and customer satisfaction...`}
          onAiClick={writeTenantPromptWithAI}
          isLoading={isWritingTenantPrompt}
        />
      )}
    </Stack>
  );
};

/**
 * @param {Object} props
 * @param {UseForm<OnboardingFormValues>} props.form
 * @param {'personPrompt'|'organizationPrompt'} props.name
 * @param {string} props.label
 * @param {string} props.description
 * @param {string} props.tooltip
 * @param {string} props.placeholder
 * @param {(ctx: {name: string, value: string}) => void} [props.onAiClick]
 */
const PromptStack = ({
  form,
  name,
  label,
  description,
  tooltip,
  placeholder,
  onAiClick,
  isLoading
}) => {
  return (
    <Stack gap={2}>
      <HStack gap={2}>
        <Text
          variant='label-caps-600-lg'
          fontWeight={600}
          fontSize='md'
          color='brandPrimary.primary'
        >
          {label}
        </Text>
        <Tooltip
          label={tooltip}
          hasArrow
        >
          <IconButton
            aria-label={`${label} info`}
            icon={<InfoIcon size='16px' />}
            color='surface.outline'
            size='xs'
            rounded='full'
            variant='reskinGhost'
            _hover={{ bgColor: 'surface.surfaceDim' }}
          />
        </Tooltip>
      </HStack>
      <Text color='surface.onSurfaceVariant'>{description}</Text>
      <Controller
        name={name}
        control={form.control}
        render={({ field }) => (
          <Textarea
            variant='filled'
            _hover={{ bgColor: 'white' }}
            mt={1}
            placeholder={placeholder}
            resize='vertical'
            minHeight='150px'
            {...field}
          />
        )}
      />
      <Button
        mt={1}
        w='fit-content'
        variant='reskinOutline'
        onClick={onAiClick}
        disabled={isLoading}
      >
        {isLoading && (
          <Spinner
            size='sm'
            mr={2}
          />
        )}
        Write with AI
      </Button>
    </Stack>
  );
};

export default PromptsStep;
